import { IconClose16 } from '@cian/ui-kit';
import { animated, config, useSpring } from '@react-spring/web';
import cn from 'classnames';
import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { ModalContext } from './ModalContext/ModalContext';
import * as styles from './SwipeModal.css';
import { useSwipe } from './useSwipe';
import { useWatchModalBodyHeight } from './useWatchModalBodyHeight';
import { ETestId } from '../../constants';

const DEFAULT_HEIGHT = 700;

interface ISwipeModalProps {
  show: boolean;
  header?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  maxHeight?: number;
  fixHeight?: boolean;
  hasOverlay?: boolean;
  hasCloseButton?: boolean;
  closeButtonClassName?: string;
  halfScreen?: boolean;
  fixedHeader?: boolean;
  testId?: string;
  className?: string;
  shouldListenSwipeDown?: boolean;
  scrollContainerRef?(element: HTMLDivElement | null): void;
  heightFromCss?: boolean;
  onSwipeUp?(): void;
  onSwipeDown?(): void;
  onClose(): void;
  onCloseButtonClick?(): void;
  onFullscreenToggle?(): void;
}

export const SwipeModal = (props: ISwipeModalProps) => {
  const {
    show,
    header,
    children,
    footer,
    maxHeight = DEFAULT_HEIGHT,
    fixHeight = false,
    halfScreen,
    fixedHeader = false,
    hasOverlay = false,
    hasCloseButton,
    closeButtonClassName,
    testId,
    className,
    shouldListenSwipeDown,
    scrollContainerRef,
    heightFromCss = false,
    onClose,
    onCloseButtonClick,
    onSwipeUp,
    onSwipeDown,
    onFullscreenToggle,
  } = props;

  const mainRef = React.useRef<null | HTMLDivElement>(null);

  const setContentNode = React.useCallback(
    (node: HTMLDivElement | null) => {
      mainRef.current = node;
      scrollContainerRef?.(node);
    },
    [scrollContainerRef],
  );

  const [{ y }, api] = useSpring(() => ({
    from: { y: DEFAULT_HEIGHT },
  }));
  const [style, animate] = useSpring(() => ({ height: 0 }), []);

  const elementHeight = useWatchModalBodyHeight(mainRef, show, maxHeight, fixHeight);

  const open = React.useCallback(() => {
    api.start({
      y: 0,
      immediate: false,
      config: config.default,
    });
  }, [api]);

  const toggleFullscreen = () => {
    if (onFullscreenToggle) {
      onFullscreenToggle();
    }
  };
  const close = React.useCallback(
    (promiseResolve?: (val: unknown) => void) => {
      api.start({
        y: elementHeight,
        immediate: false,
        config: { ...config.default, velocity: 0 },
      });

      setTimeout(() => {
        onClose();
        animate({ height: 0 });
        if (promiseResolve) {
          promiseResolve(0);
        }
      }, 400);
    },
    [animate, api, elementHeight, onClose],
  );

  useSwipe({
    y,
    api,
    maxHeight,
    open,
    close,
    toggleFullscreen,
    halfScreen,
  });

  React.useEffect(() => {
    if (show) {
      open();
    }
  }, [show, open]);

  animate({ height: elementHeight });

  const promisifiedClose = useCallback(() => new Promise(res => close(res)), [close]);
  const modalContextValue = useMemo(() => ({ closeModal: promisifiedClose }), [promisifiedClose]);

  const [startTouch, setStartTouch] = React.useState({ x: 0, y: 0 });

  const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
    setStartTouch({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY });
  }, []);

  const handleTouchEnd = React.useCallback(
    async (e: React.TouchEvent) => {
      const endTouchX = e.changedTouches[0].clientX;
      const endTouchY = e.changedTouches[0].clientY;
      const diffY = Math.abs(startTouch.y - endTouchY);
      const diffX = Math.abs(startTouch.x - endTouchX);

      // если смещение по X больше определенного порога, не считаем жест за вертикальный свайп
      if (diffX > diffY / 4) {
        return;
      }

      const isSwipeDown = endTouchY - startTouch.y > 50;
      const isSwipeUp = startTouch.y - endTouchY > 50;
      if (isSwipeDown && shouldListenSwipeDown) {
        await promisifiedClose();
        onClose();
        if (onSwipeDown) {
          onSwipeDown();
        }
      } else if (isSwipeUp) {
        if (onSwipeUp) {
          onSwipeUp();
        }
      }
    },
    [startTouch, shouldListenSwipeDown, promisifiedClose, onClose, onSwipeUp, onSwipeDown],
  );

  const handleCloseButtonClick = React.useCallback(async () => {
    await promisifiedClose();
    if (onCloseButtonClick) {
      onCloseButtonClick();
    }
  }, [onCloseButtonClick, promisifiedClose]);

  return (
    <ModalContext.Provider value={modalContextValue}>
      {hasOverlay && show && (
        <div className={styles['overlay']} onClick={() => close()} data-testid={ETestId.SwipeModalOverlay}></div>
      )}
      <animated.div
        className={cn(
          styles['content'],
          {
            [styles['content_half-screen']]: halfScreen,
            [styles['content_hidden']]: !show,
          },
          className,
        )}
        data-testid={testId}
        id={testId}
        style={heightFromCss ? { y } : { y, maxHeight, ...style }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={cn(styles['content-wrapper'], {
            [styles['content-wrapper_fixed-header']]: fixedHeader,
            [styles['content-wrapper_half-screen']]: halfScreen,
            [styles['content-wrapper_css-height']]: heightFromCss,
          })}
          ref={setContentNode}
          style={heightFromCss ? undefined : { maxHeight }}
          id="ListingMobileContent"
        >
          {header && (
            <header
              className={cn(styles['header'], { [styles['header_reduced']]: !header })}
              data-testid={ETestId.SwipeModalHeader}
            >
              {header}
            </header>
          )}
          {hasCloseButton && (
            <button
              type="button"
              className={cn(styles['close-button'], closeButtonClassName)}
              onClick={handleCloseButtonClick}
              data-testid={ETestId.SwipeModalCloseButton}
            >
              <span className={styles['close-icon']}>
                <IconClose16 width={12} height={12} />
              </span>
            </button>
          )}
          {children && (
            <main
              className={cn(styles['main'], { [styles['main_fixed-header']]: fixedHeader })}
              data-testid={ETestId.SwipeModalChildren}
            >
              {children}
            </main>
          )}
          {footer && (
            <footer className={styles['footer']} data-testid={ETestId.SwipeModalFooter}>
              {footer}
            </footer>
          )}
        </div>
      </animated.div>
    </ModalContext.Provider>
  );
};
